"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTreeNodeFiltering = void 0;
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const catalogs_1 = require("../../../store/catalogs");
const selected_l1_categories_1 = require("../../../store/shared/selected-l1-categories");
const store_1 = require("../../../../store");
const useTreeNodeFiltering = () => {
    const dispatch = (0, react_redux_1.useDispatch)();
    const selectedCatalogId = (0, store_1.useSelectedCatalogId)();
    const { showEmptyNodes, showStrategyChanges } = (0, store_1.useProductTreeFilters)();
    // Get filtering state from Redux
    const { extendedFilter, exclusiveFilter = [] } = (0, catalogs_1.useSelectedL1CategoriesAndTerminalNodesData)();
    // Computed values
    const allowedL1Ids = (0, react_1.useMemo)(() => {
        if (!(extendedFilter === null || extendedFilter === void 0 ? void 0 : extendedFilter.length))
            return undefined;
        const l1Ids = [];
        extendedFilter.forEach(({ id }) => {
            if (id) {
                l1Ids.push(id);
            }
        });
        return l1Ids;
    }, [extendedFilter]);
    const hasExtendedFilterApplied = (0, react_1.useMemo)(() => (extendedFilter && extendedFilter.length > 0), [extendedFilter]);
    // High-level operations
    const setExtendedFilter = (0, react_1.useCallback)((selection) => {
        let categoriesToSelect = selection;
        // Convert from GroupedOption to SelectedCategory if needed
        if (Array.isArray(selection) && (0, selected_l1_categories_1.isGroupedOptionArray)(selection)) {
            categoriesToSelect = selection.map(selected_l1_categories_1.convertGroupedOptionToCategory);
        }
        // Detect removed ids and clear any exclusive visibility for them
        const prevIds = (extendedFilter || []).map((c) => c.id);
        const nextIds = (categoriesToSelect || []).map((c) => c.id);
        const removed = prevIds.filter((id) => id && !nextIds.includes(id));
        removed.forEach((id) => {
            if (id) {
                dispatch((0, catalogs_1.removeFromExclusiveFilterForProductTreeTaxonomy)(selectedCatalogId, id));
            }
        });
        dispatch((0, catalogs_1.setTreeNodeFilteringStateForProductTreeTaxonomy)(selectedCatalogId, { extendedFilter: categoriesToSelect }));
    }, [dispatch, selectedCatalogId, extendedFilter]);
    const clearExtendedFilter = (0, react_1.useCallback)(() => {
        dispatch((0, catalogs_1.setTreeNodeFilteringStateForProductTreeTaxonomy)(selectedCatalogId, { extendedFilter: [] }));
    }, [dispatch, selectedCatalogId]);
    const addToExtendedFilter = (0, react_1.useCallback)((nodeId) => {
        const currentFilter = extendedFilter || [];
        const nodeExists = currentFilter.some((category) => category.id === nodeId);
        if (!nodeExists) {
            const newFilter = [...currentFilter, { id: nodeId }];
            setExtendedFilter(newFilter);
        }
    }, [extendedFilter, setExtendedFilter]);
    // Exclusive filter operations
    const addToExclusiveFilter = (0, react_1.useCallback)((nodeId) => {
        dispatch((0, catalogs_1.addToExclusiveFilterForProductTreeTaxonomy)(selectedCatalogId, nodeId));
    }, [dispatch, selectedCatalogId]);
    const removeFromExclusiveFilter = (0, react_1.useCallback)((nodeId) => {
        dispatch((0, catalogs_1.removeFromExclusiveFilterForProductTreeTaxonomy)(selectedCatalogId, nodeId));
    }, [dispatch, selectedCatalogId]);
    const clearExclusiveFilter = (0, react_1.useCallback)(() => {
        dispatch((0, catalogs_1.clearExclusiveFilterForProductTreeTaxonomy)(selectedCatalogId));
    }, [dispatch, selectedCatalogId]);
    // Utility functions
    const isNodeVisible = (0, react_1.useCallback)((nodeId) => {
        // Always visible if in exclusive filter (check both top-level and children)
        const isInExclusiveFilter = exclusiveFilter.some((category) => { var _a; return (category.id === nodeId || ((_a = category.children) === null || _a === void 0 ? void 0 : _a.includes(nodeId))); });
        if (isInExclusiveFilter) {
            return true;
        }
        // If extended filter is applied, check if node is in allowed L1 list
        if (hasExtendedFilterApplied) {
            return (allowedL1Ids === null || allowedL1Ids === void 0 ? void 0 : allowedL1Ids.includes(nodeId)) || false;
        }
        // If no extended filter is applied, don't override visibility - let showEmptyNodes logic handle it
        return false;
    }, [exclusiveFilter, hasExtendedFilterApplied, allowedL1Ids]);
    const shouldShowNodeInTree = (0, react_1.useCallback)((nodeId, options) => {
        const { isChanged, hasContent, hasManualSelection, ignoreManualVisibility, } = options;
        if (hasManualSelection) {
            return true;
        }
        const isVisibleByManualFilter = ignoreManualVisibility ? false : isNodeVisible(nodeId);
        if (showStrategyChanges) {
            return !!isChanged || isVisibleByManualFilter;
        }
        return isVisibleByManualFilter || showEmptyNodes || !!hasContent;
    }, [isNodeVisible, showStrategyChanges, showEmptyNodes]);
    const extendedFilterMemo = (0, react_1.useMemo)(() => extendedFilter || [], [extendedFilter]);
    // Return public API
    return {
        // State
        extendedFilter: extendedFilterMemo,
        exclusiveFilter,
        allowedL1Ids,
        hasExtendedFilterApplied,
        // Extended filter operations
        setExtendedFilter,
        clearExtendedFilter,
        addToExtendedFilter,
        // Exclusive filter operations
        addToExclusiveFilter,
        removeFromExclusiveFilter,
        clearExclusiveFilter,
        // Utility
        isNodeVisible,
        shouldShowNodeInTree,
        /** @deprecated Use extendedFilter instead. Will be removed in future version. */
        selectedCategories: extendedFilterMemo,
        /** @deprecated Use setExtendedFilter instead. Will be removed in future version. */
        handleCategorySelectionChanged: setExtendedFilter,
    };
};
exports.useTreeNodeFiltering = useTreeNodeFiltering;
